import React, { useRef, useEffect, useState } from 'react';
import { Info, Users, UserPlus, Trash2, FilePlus, FileText, PlusCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface Treatment {
  treatmentTitle: string;
  description: string;
}

interface TeamAndSpecialtiesFormProps {
  persistOnChange?: boolean;
  technicalResponsible: string;
  setTechnicalResponsible: (val: string) => void;
  treatments: Treatment[];
  setTreatments: (treatments: Treatment[]) => void;
}

// Interface para os estados temporários dos tratamentos
interface TreatmentInputs {
  [key: string]: {
    treatmentTitle: string;
    description: string;
  }
}

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative inline-flex group" tabIndex={0} aria-label={text}>
    <Info className="h-3.5 w-3.5 text-gray-400 transition-colors group-hover:text-sminy-primary" />
    <span
      role="tooltip"
      className="pointer-events-none absolute left-1/2 top-5 z-20 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs font-normal leading-5 text-gray-700 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus:opacity-100"
    >
      {text}
    </span>
  </span>
);

// Helper components (seguindo padrão perfeito)
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string; tooltip: string; required?: boolean }> = ({
  label,
  children,
  hint,
  tooltip,
  required = true
}) => {
  const { isDark } = useTheme();
  return (
    <label className="block text-sm">
      <span className={`mb-1 flex items-center gap-1.5 ${
        isDark ? 'text-gray-300' : 'text-gray-700'
      }`}>
        <span>{label}{required && <span className="text-red-500"> *</span>}</span>
        <InfoTooltip text={tooltip} />
      </span>
      {children}
      {hint && <span className={`mt-1 block text-[11px] ${
        isDark ? 'text-gray-400' : 'text-gray-500'
      }`}>{hint}</span>}
    </label>
  );
};

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const { isDark } = useTheme();
  return (
    <input
      ref={ref}
      {...props}
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
        isDark
          ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
          : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
      } ${props.className ?? ""}`}
    />
  );
});

const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>((props, ref) => {
  const { isDark } = useTheme();
  return (
    <textarea
      ref={ref}
      {...props}
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
        isDark
          ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
          : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
      } ${props.className ?? ""}`}
    />
  );
});


const TeamAndSpecialtiesForm: React.FC<TeamAndSpecialtiesFormProps> = ({
  persistOnChange = false,
  technicalResponsible,
  setTechnicalResponsible,
  treatments,
  setTreatments
}) => {
  const { isDark } = useTheme();
  const [pendingRemoval, setPendingRemoval] = useState<number | null>(null);
  const removalDialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (pendingRemoval !== null) removalDialog.current?.showModal();
    else removalDialog.current?.close();
  }, [pendingRemoval]);


  // Ref para o input do responsável técnico
  const responsibleRef = useRef<HTMLInputElement>(null);

  // Estado local para armazenar os valores dos campos de tratamentos
  const [treatmentInputs, setTreatmentInputs] = useState<TreatmentInputs>({});

  // Inicializar valor do responsável técnico
  useEffect(() => {
    if (responsibleRef.current) {
      responsibleRef.current.value = technicalResponsible;
    }
  }, [technicalResponsible]);

  // Inicializar o estado treatmentInputs com os valores dos treatments
  useEffect(() => {
    const initialInputs: TreatmentInputs = {};
    treatments.forEach((treatment, index) => {
      initialInputs[`treatment-${index}`] = {
        treatmentTitle: treatment.treatmentTitle,
        description: treatment.description
      };
    });
    setTreatmentInputs(initialInputs);
  }, []);

  const addTreatment = () => {
    // Salvar posição atual do scroll
    const currentScrollY = window.scrollY;

    const newIndex = treatments.length;
    const newTreatment = { treatmentTitle: '', description: '' };

    // Criar uma cópia do estado atual para evitar problemas de concorrência
    const updatedTreatments = [...treatments, newTreatment];

    // Atualizar o estado local para incluir o novo item
    const newInputs = { ...treatmentInputs };
    newInputs[`treatment-${newIndex}`] = {
      treatmentTitle: '',
      description: ''
    };
    setTreatmentInputs(newInputs);

    // Atualizar o estado global
    setTreatments(updatedTreatments);

    // Manter posição de scroll após a atualização
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const removeTreatment = (index: number) => {
    // Salvar posição atual do scroll
    const currentScrollY = window.scrollY;

    // Remover do estado local
    const newInputs = { ...treatmentInputs };
    delete newInputs[`treatment-${index}`];

    // Reorganizar as chaves após a remoção
    const updatedInputs: TreatmentInputs = {};
    const newTreatments = treatments.filter((_, i) => i !== index);

    newTreatments.forEach((treatment, i) => {
      updatedInputs[`treatment-${i}`] = {
        treatmentTitle: treatment.treatmentTitle,
        description: treatment.description
      };
    });

    setTreatmentInputs(updatedInputs);
    setTreatments(newTreatments);

    // Manter posição de scroll após a atualização
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const updateTreatmentField = (index: number, field: keyof Treatment, value: string) => {
    const key = `treatment-${index}`;

    // Atualizar apenas se o valor realmente mudou, para evitar re-renderizações desnecessárias
    if (treatments[index][field] !== value) {
      // Atualizar o estado local
      setTreatmentInputs(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          [field]: value
        }
      }));

      // Atualizar o estado global
      const updatedTreatments = [...treatments];
      updatedTreatments[index] = {
        ...updatedTreatments[index],
        [field]: value
      };

      setTreatments(updatedTreatments);
    }
  };

  return (
    <div className="space-y-4">
      {persistOnChange && <dialog ref={removalDialog} className="register-removal-dialog" aria-labelledby="remove-treatment-title" onCancel={() => setPendingRemoval(null)}>
        <h2 id="remove-treatment-title">Remover tratamento?</h2>
        <p>O tratamento será removido deste cadastro. Você pode adicioná-lo novamente depois.</p>
        <div className="register-actions flex gap-3">
          <button type="button" autoFocus onClick={() => setPendingRemoval(null)} className="border rounded-xl px-4">Cancelar</button>
          <button type="button" className="bg-red-600 text-white rounded-xl px-4" onClick={() => { if (pendingRemoval !== null) removeTreatment(pendingRemoval); setPendingRemoval(null); }}>Remover</button>
        </div>
      </dialog>}
      {/* Responsável técnico - seguindo padrão */}
      <Field label="Responsável Técnico" tooltip="Profissional responsável pela clínica, incluindo CRO quando disponível.">
        <Input
          ref={responsibleRef}
          type="text"
          required
          placeholder="Ex: Dr. João Silva (CRO-SP 12345)"
          defaultValue={technicalResponsible}
          onChange={persistOnChange ? (e) => setTechnicalResponsible(e.target.value) : undefined}
          onBlur={() => responsibleRef.current && setTechnicalResponsible(responsibleRef.current.value)}
        />
      </Field>

      {/* Tratamentos - seguindo padrão */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className={`text-sm font-medium ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Tratamentos Oferecidos <span className="text-red-500">*</span>
            <span className="ml-1.5 align-middle"><InfoTooltip text="Adicione os tratamentos que o assistente pode apresentar e qualificar durante a conversa." /></span>
          </span>
          <button
            type="button"
            onClick={addTreatment}
            className="px-3 py-1.5 bg-sminy-primary text-white rounded-xl text-xs hover:bg-sminy-primary/90 transition-colors"
          >
            + Adicionar
          </button>
        </div>

        {treatments.length === 0 ? (
          <div className={`rounded-2xl border p-6 text-center ${
            isDark
              ? 'border-gray-600 bg-gray-800/50'
              : 'border-gray-200 bg-gray-50'
          }`}>
            <p className={`text-sm ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>Adicione pelo menos um tratamento para continuar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {treatments.map((treatment, index) => {
              const key = `treatment-${index}`;
              const inputs = treatmentInputs[key] || { treatmentTitle: treatment.treatmentTitle, description: treatment.description };

              return (
                <div key={index} className={`p-4 rounded-xl border ${
                  isDark
                    ? 'border-gray-600 bg-gray-800/50'
                    : 'border-gray-200 bg-gray-50/50'
                }`}>
                  <div className="flex justify-between items-center mb-3">
                    <span className={`text-sm font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>Tratamento #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => persistOnChange ? setPendingRemoval(index) : removeTreatment(index)}
                      className="text-red-500 hover:text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 transition-colors"
                    >
                      Remover
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Nome do Tratamento" tooltip="Nome do procedimento ou especialidade oferecida pela clínica.">
                      <Input
                        type="text"
                        required
                        placeholder="Ex: Implante Dentário"
                        key={persistOnChange ? 'title' : inputs.treatmentTitle}
                        {...(persistOnChange ? { value: treatment.treatmentTitle } : { defaultValue: inputs.treatmentTitle })}
                        onChange={persistOnChange ? (e) => updateTreatmentField(index, 'treatmentTitle', e.target.value) : undefined}
                        onBlur={(e) => updateTreatmentField(index, 'treatmentTitle', e.target.value)}
                      />
                    </Field>

                    <Field label="Descrição" tooltip="Explicação curta para orientar o assistente ao responder dúvidas do paciente.">
                      <Textarea
                        required
                        placeholder="Descreva o tratamento..."
                        key={persistOnChange ? 'description' : inputs.description}
                        {...(persistOnChange ? { value: treatment.description } : { defaultValue: inputs.description })}
                        onChange={persistOnChange ? (e) => updateTreatmentField(index, 'description', e.target.value) : undefined}
                        onBlur={(e) => updateTreatmentField(index, 'description', e.target.value)}
                        rows={5}
                      />
                    </Field>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamAndSpecialtiesForm;
